import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, MapPin, Home as HomeIcon, Maximize, Phone, Mail, Check, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { propertiesApi } from '../services/api';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../components/ui/carousel';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [carouselApi, setCarouselApi] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    loadProperty();
  }, [id]);

  useEffect(() => {
    if (!carouselApi) return;

    const updateScrollState = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };

    updateScrollState();
    carouselApi.on('select', updateScrollState);
    carouselApi.on('reInit', updateScrollState);

    return () => {
      carouselApi.off('select', updateScrollState);
    };
  }, [carouselApi]);

  const loadProperty = async () => {
    try {
      setLoading(true);
      const data = await propertiesApi.getById(id);
      setProperty(data);
    } catch (error) {
      console.error('Error loading property:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price) + ' CHF';
  };

  // Convert Google Drive view links to direct download links
  const getDownloadUrl = (url) => {
    if (!url) return url;
    
    // Check if it's a Google Drive link
    const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
    if (driveMatch) {
      const fileId = driveMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }
    
    // Check for other Google Drive format
    const driveMatch2 = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
    if (driveMatch2) {
      const fileId = driveMatch2[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }
    
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-32 pb-16 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B2942]"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl font-bold text-slate-800 mb-4">Bien non trouvé</h1>
            <p className="text-slate-600 mb-8">Ce bien n'existe pas ou n'est plus disponible.</p>
            <Button asChild>
              <Link to="/" className="flex items-center gap-2">
                <ArrowLeft className="w-5 h-5" />
                Retour à l'accueil
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Back Button */}
        <div className="pt-24 pb-4 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Button asChild variant="ghost" className="group">
              <Link to="/a-vendre" className="flex items-center gap-2">
                <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                Retour aux biens
              </Link>
            </Button>
          </div>
        </div>

        {/* Property Content */}
        <section className="py-8 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Image Carousel - Full Width on Top */}
            <div className="relative mb-8">
              {(() => {
                const allImages = [property.image, ...(property.images || [])].filter(Boolean);
                
                if (allImages.length === 1) {
                  return (
                    <div className="relative overflow-hidden rounded-2xl bg-gray-200">
                      <img
                        src={allImages[0]}
                        alt={property.title}
                        loading="lazy"
                        className="w-full h-[350px] md:h-[450px] lg:h-[500px] object-cover"
                      />
                      {property.status === 'reserve' ? (
                        <div className="absolute top-0 left-0 w-40 h-40 overflow-hidden pointer-events-none z-10">
                          <div
                            className="absolute bg-[#8B2942] text-white text-base font-bold uppercase tracking-widest text-center shadow-lg"
                            style={{
                              width: '220px',
                              padding: '10px 0',
                              transform: 'rotate(-45deg)',
                              left: '-60px',
                              top: '40px',
                            }}
                          >
                            Réservé
                          </div>
                        </div>
                      ) : (
                        <Badge className="absolute top-4 left-4 text-sm bg-[#8B2942] hover:bg-[#6d2033] text-white z-10">
                          {property.status === 'vendu' ? 'Vendu' : 'En vente'}
                        </Badge>
                      )}
                    </div>
                  );
                }
                
                return (
                  <div className="relative">
                    <Carousel
                      setApi={setCarouselApi}
                      opts={{
                        align: 'start',
                        loop: true,
                      }}
                      className="w-full"
                    >
                      <CarouselContent>
                        {allImages.map((img, index) => (
                          <CarouselItem key={index}>
                            <div className="relative overflow-hidden rounded-2xl bg-gray-200">
                              <img
                                src={img}
                                loading="lazy"
                                alt={`${property.title} - Image ${index + 1}`}
                                className="w-full h-[350px] md:h-[450px] lg:h-[500px] object-cover"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </Carousel>
                    
                    {/* Navigation Buttons */}
                    <button
                      onClick={() => carouselApi?.scrollPrev()}
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white hover:scale-110 transition-all duration-300 drop-shadow-lg"
                      aria-label="Image précédente"
                    >
                      <ChevronLeft className="h-8 w-8" />
                    </button>
                    
                    <button
                      onClick={() => carouselApi?.scrollNext()}
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white hover:scale-110 transition-all duration-300 drop-shadow-lg"
                      aria-label="Image suivante"
                    >
                      <ChevronRight className="h-8 w-8" />
                    </button>
                    
                    {/* Badge and Image Counter */}
                    {property.status === 'reserve' ? (
                      <div className="absolute top-0 left-0 w-40 h-40 overflow-hidden pointer-events-none z-10">
                        <div
                          className="absolute bg-[#8B2942] text-white text-base font-bold uppercase tracking-widest text-center shadow-lg"
                          style={{
                            width: '220px',
                            padding: '10px 0',
                            transform: 'rotate(-45deg)',
                            left: '-60px',
                            top: '40px',
                          }}
                        >
                          Réservé
                        </div>
                      </div>
                    ) : (
                      <Badge className="absolute top-4 left-4 text-sm bg-[#8B2942] hover:bg-[#6d2033] text-white z-10">
                        {property.status === 'vendu' ? 'Vendu' : 'En vente'}
                      </Badge>
                    )}
                    
                    <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm z-10">
                      {currentSlide + 1} / {allImages.length}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Content Below Image */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#8B2942] mb-4">
                {property.title}
              </h1>

              <div className="flex items-center gap-2 text-slate-600 mb-6">
                <MapPin className="w-5 h-5" />
                <span className="text-lg">{property.location}</span>
              </div>

              {property.status !== 'vendu' && (
                <div className="text-4xl font-bold text-[#8B2942] mb-8">
                  {formatPrice(property.price)}
                </div>
              )}

              {/* Features */}
              {property.features && property.features.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-[#8B2942] mb-3">Caractéristiques</h2>
                  <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {property.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-gray-600">
                        <Check className="w-5 h-5 text-[#8B2942]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Download Brochure */}
              {property.brochure_url && (
                <div className="mb-8">
                  <Button 
                    asChild 
                    size="lg" 
                    className="w-full md:w-auto bg-[#8B2942] hover:bg-[#6d2033]"
                  >
                    <a 
                      href={getDownloadUrl(property.brochure_url)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      download
                      className="flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      Télécharger la brochure (PDF)
                    </a>
                  </Button>
                </div>
              )}

              {/* Description */}
              {property.description && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-[#8B2942] mb-3">Description</h2>
                  <div className="text-slate-600 leading-relaxed">
                    {property.description.split('\n').map((line, index) => {
                      if (line.trim().endsWith(':')) {
                        return (
                          <div key={index} className="font-semibold text-[#8B2942] mt-4 mb-2">
                            {line}
                          </div>
                        );
                      }
                      return (
                        <div key={index} className={line.trim() === '' ? 'h-2' : ''}>
                          {line || '\u00A0'}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Contact CTA */}
              {property.status !== 'vendu' && (
                <Card className="bg-slate-50">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#8B2942] mb-4">
                      Intéressé(e) par ce bien ?
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button asChild className="flex-1 bg-[#8B2942] hover:bg-[#6d2033]">
                        <a href="tel:+41791337898" className="flex items-center justify-center gap-2">
                          <Phone className="w-5 h-5" />
                          Appeler
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="flex-1">
                        <Link to="/contact" className="flex items-center justify-center gap-2">
                          <Mail className="w-5 h-5" />
                          Envoyer un message
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PropertyDetailPage;
